import { AccountSubType, AccountType } from "../models/AccountBase";
import { PlanData } from "../models/PlanBase";
import {
  hasValidPlanWith,
  SubscriptionWithPlan,
} from "../subscriptions/hasValidPlanWith";

export enum FeatureAccess {
  GetTaxSummary = "get-tax-summary",

  /**
   * download the crypto tax report pdf
   */
  GetTaxReport = "get-tax-report",

  GetTaxOutcomeReport = "get-tax-outcome-report",
  GetTransactionReport = "get-transaction-report",
  GetRemainingParcelsReport = "get-remaining-parcels-report",
  GetBglSyncXml = "get-bgl-sync-xml",
  GetHoldingStatement = "get-holding-statement",

  WriteBglSync = "write-bgl-sync",

  FeatureTaxLtfo = "feat-tax-ltfo",
  FeatureTaxHifo = "feat-tax-hifo",
  FeatureTaxLifo = "feat-tax-lifo",
}

export const permissionDeniedText: {
  [Property in keyof PermissionDeniedReason]: string;
} = {
  noValidPlan: "You need to upgrade your plan to access this feature.",
  transactionCap:
    "You have more transactions than your current plan supports. You must upgrade your plan to access this feature.",
};

type AccountForPermissions =
  | { accountType: AccountType; accountSubType: AccountSubType }
  | undefined;

export type PermissionProps = {
  feature: FeatureAccess;
  subscriptions: SubscriptionWithPlan[];
  transactionCount: number | undefined;
  account: AccountForPermissions;
};

export function permissionFor(props: PermissionProps): {
  hasPermission: boolean;
  deniedReason?: PermissionDeniedReason;
} {
  let deniedReason: PermissionDeniedReason;

  const capExceeded = !hasValidPlanWith(props.subscriptions, ({ plan }) =>
    satisfiesTransactionCount({
      plan,
      transactionCount: props.transactionCount,
    }),
  );

  switch (props.feature) {
    case FeatureAccess.GetTaxReport:
    case FeatureAccess.GetTaxSummary:
      deniedReason = {
        transactionCap: capExceeded,
        noValidPlan: !hasValidPlanWith(props.subscriptions, ({ plan }) =>
          satisfiesAccountSettings({ account: props.account, plan }),
        ),
      };
      break;
    case FeatureAccess.GetTaxOutcomeReport:
    case FeatureAccess.GetTransactionReport:
    case FeatureAccess.GetRemainingParcelsReport:
    case FeatureAccess.GetHoldingStatement:
      deniedReason = {
        transactionCap: capExceeded,
        noValidPlan: !hasValidPlanWith(
          props.subscriptions,
          ({ plan }) =>
            satisfiesAccountSettings({ account: props.account, plan }) &&
            plan.features.assuranceReports,
        ),
      };
      break;
    case FeatureAccess.WriteBglSync:
    case FeatureAccess.GetBglSyncXml:
      deniedReason = {
        transactionCap: capExceeded,
        noValidPlan: !hasValidPlanWith(
          props.subscriptions,
          ({ plan }) =>
            satisfiesAccountSettings({ account: props.account, plan }) &&
            plan.features.premiumIntegrations,
        ),
      };
      break;
    case FeatureAccess.FeatureTaxLtfo:
    case FeatureAccess.FeatureTaxHifo:
    case FeatureAccess.FeatureTaxLifo:
      deniedReason = {
        // exceeding tx cap is okay, user can still change the parcel method
        noValidPlan: !hasValidPlanWith(
          props.subscriptions,
          ({ plan }) =>
            satisfiesAccountSettings({ account: props.account, plan }) &&
            plan.features.taxOptimisation,
        ),
      };
      break;
  }

  const result = {
    hasPermission: !Object.values(deniedReason).some(Boolean),
    deniedReason,
  };
  return result;
}

export function getPermissionDeniedText(
  reason: PermissionDeniedReason | undefined,
) {
  return (
    !reason ? ""
    : reason.noValidPlan ? permissionDeniedText.noValidPlan
    : reason.transactionCap ? permissionDeniedText.transactionCap
    : ""
  );
}

// permission can be denied for multiple reasons
export type PermissionDeniedReason = {
  noValidPlan?: boolean;
  transactionCap?: boolean;
  noBillingAccess?: boolean;
};

/**
 * Check if a plan satisfies all requirements.
 */
export const satisfiesAllRequirements = ({
  plan,
  account,
  transactionCount,
}: {
  account: AccountForPermissions;
  plan: PlanData;
  transactionCount: number | undefined;
}) =>
  satisfiesAccountSettings({ account, plan }) &&
  satisfiesTransactionCount({ plan, transactionCount });

/**
 * Check if a plan can support a certain number of transactions.
 */
export const satisfiesTransactionCount = ({
  plan,
  transactionCount,
}: {
  plan: PlanData;
  transactionCount: number | undefined;
}) =>
  transactionCount !== undefined &&
  plan.features.transactionLimit >= transactionCount;

/**
 * Check if the plan satisfies the current entity type and activity type
 * on the account.
 */
export const satisfiesAccountSettings = ({
  plan,
  account,
}: {
  account: AccountForPermissions;
  plan: PlanData;
}) =>
  satisfiesEntityType({ account, plan }) &&
  satisfiesActivityType({ account, plan });

/**
 * Does the account have a plan sufficient for its current account type
 */
export const satisfiesEntityType = ({
  plan,
  account,
}: {
  account: AccountForPermissions;
  plan: PlanData;
}) =>
  !!account &&
  (!isEntityAccountType(account.accountType) || plan.features.entities);

/**
 * DOes the account have a plan sufficient for its current activity type
 */
export const satisfiesActivityType = ({
  plan,
  account,
}: {
  account: AccountForPermissions;
  plan: PlanData;
}) =>
  !!account &&
  (!isTradingStockAccountSubType(account.accountSubType) ||
    plan.features.tradingStock);

export const isEntityAccountType = (accountType: AccountType) =>
  [AccountType.SMSF, AccountType.Company, AccountType.Trust].includes(
    accountType,
  );

export const isTradingStockAccountSubType = (accountSubType: AccountSubType) =>
  [AccountSubType.Trader].includes(accountSubType);
